import React, { useMemo, useEffect, useState } from 'react';
import Chart from 'react-apexcharts';

const CategoryBarChart = ({
    Data,
    totalCounts,
    onDataPointClick,
    isLoading,
    containerWidth = 50,
    isFeedback,
    onReady,
    showAll
}) => {
    const [topN, setTopN] = useState(20);

    const colorPalette = [
        '#D0CEFB', '#FEB4AA', '#B8DFFE', '#FCE7B8', '#FCD4E3',
        '#C5E1A5', '#FFE082', '#FFAB91', '#CE93D8', '#90CAF9',
        '#A5D6A7', '#FFCC80', '#EF9A9A', '#B39DDB', '#81D4FA',
        '#C5E1A5', '#FFD54F', '#BCAAA4', '#E1BEE7', '#80DEEA',
        '#DCEDC8', '#FFF59D', '#FFCCBC', '#D1C4E9', '#B2EBF2'
    ];

    const chartData = useMemo(() => {
        if (!Data || (Array.isArray(Data) ? Data.length === 0 : Object.keys(Data).length === 0)) {
            return null;
        }

        let items = [];

        if (isFeedback === true && Array.isArray(Data)) {
            items = Data
                .filter(item => {
                    const hasValidAttribute = item.attribute_value !== null &&
                        item.attribute_value !== undefined &&
                        item.attribute_value !== '';
                    const count = item.count !== undefined ? item.count : (item.value || 0);
                    const hasValidCount = count > 0;

                    return hasValidAttribute && hasValidCount;
                })
                .map((item, idx) => {
                    const attributeValue = item.attribute_value;
                    const count = item.count !== undefined ? item.count : (item.value || 0);
                    return {
                        name: String(attributeValue).replace(/_/g, ' '),
                        value: count,
                        key: attributeValue,
                        rawData: item,
                        index: idx
                    };
                });
        } else {
            items = Object.entries(Data)
                .map(([key, categoryData], idx) => ({
                    name: key.replace(/_/g, ' '),
                    value: categoryData.count || 0,
                    key: key,
                    rawData: categoryData,
                    index: idx
                }))
                .filter(item => item.value > 0);
        }

        // Sort by value descending
        items.sort((a, b) => b.value - a.value);

        // Limit to top N
        if (!showAll) {
            return items.slice(0, topN);
        }
        return items;
    }, [Data, isFeedback, showAll, topN]);

    const totalValue = useMemo(() => {
        if (!chartData) return 0;
        return chartData.reduce((sum, item) => sum + item.value, 0);
    }, [chartData]);

    // Calculate dynamic height for vertical bars
    const chartHeight = useMemo(() => {
        if (!chartData) return 800;

        const itemCount = chartData.length;
        if (itemCount <= 5) {
            setTopN(5);
            return 400;
        };
        if (itemCount <= 10) {
            setTopN(10);
            return 600;
        };
        if (itemCount <= 20) {
            setTopN(20);
            return 750;
        };
        return 790;
    }, [chartData]);

    const chartOptions = useMemo(() => {
        if (!chartData) return {};

        const categories = chartData.map(item => {
            const name = item.name;
            return name.split(' ').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ');
        });

        const counts = chartData.map(item => item.value);

        const dynamicColors = chartData.map(
            (_, index) => colorPalette[index % colorPalette.length]
        );

        return {
            series: [{
                name: 'Count',
                data: counts
            }],
            options: {
                chart: {
                    type: 'bar',
                    height: chartHeight,
                    toolbar: {
                        show: false
                    },
                    events: {
                        dataPointSelection: (event, chartContext, config) => {
                            if (onDataPointClick && chartData) {
                                const dataPointIndex = config.dataPointIndex;
                                const selectedItem = chartData[dataPointIndex];

                                onDataPointClick(null, null, {
                                    dataPointIndex: selectedItem.index,
                                    selectedCategory: selectedItem.key,
                                    categoryData: selectedItem.rawData
                                });
                            }
                        },
                        mounted: () => {
                            if (typeof onReady === 'function') {
                                setTimeout(() => onReady(), 100);
                            }
                        }
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 800
                    }
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        borderRadius: 4,
                        // barHeight: `${chartData.length > 10 ? 20 : 40}px`,
                        barHeight: `${chartData.length > 25
                            ? 10
                            : chartData.length > 10
                                ? 25
                                : 40
                            }px`,
                        dataLabels: {
                            position: 'top'
                        },
                        distributed: true
                    }
                },

                colors: dynamicColors,

                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toLocaleString();
                    },
                    offsetY: 0,
                    offsetX: 30,
                    style: {
                        fontSize: '11px',
                        colors: ['#304758'],
                        fontWeight: 600
                    }
                },
                yaxis: {
                    labels: {
                        show: true,
                        style: {
                            fontSize: '12px',
                            colors: '#666'
                        },
                        trim: false,
                        maxWidth: undefined
                    },
                    axisBorder: {
                        show: true,
                        color: '#e0e0e0'
                    },
                    axisTicks: {
                        show: true,
                        color: '#e0e0e0'
                    }
                },
                xaxis: {
                    categories: categories,
                    labels: {
                        show: true,
                        style: {
                            fontSize: '11px',
                            colors: '#333',
                            fontWeight: 500
                        },
                        formatter: function (val) {
                            return val ? val.toLocaleString() : '';
                        }
                    }
                },
                grid: {
                    show: true,
                    borderColor: '#ECEAFC',
                    strokeDashArray: 0,
                    position: 'back',
                    xaxis: {
                        lines: {
                            show: false
                        }
                    },
                    yaxis: {
                        lines: {
                            show: true
                        }
                    },
                    padding: {
                        top: 0,
                        right: 20,
                        bottom: 0,
                        left: 10
                    }
                },
                tooltip: {
                    enabled: true,
                    theme: 'light',
                    y: {
                        formatter: function (val, { dataPointIndex }) {
                            if (!chartData || !chartData[dataPointIndex]) return val.toLocaleString();

                            const percentage = totalValue > 0
                                ? ((val / totalValue) * 100).toFixed(2)
                                : 0;

                            return `${val.toLocaleString()} (${percentage}%)`;
                        },
                        title: {
                            formatter: () => 'Count: '
                        }
                    },
                    x: {
                        show: true
                    }
                },
                legend: {
                    show: false
                },
                states: {
                    hover: {
                        filter: {
                            type: 'darken',
                            value: 0.15
                        }
                    }
                }
            },

        };
    }, [chartData, onDataPointClick, totalValue, onReady, chartHeight]);

    useEffect(() => {
        if (chartData && typeof onReady === 'function') {
            setTimeout(() => onReady(), 50);
        } else if (!chartData && !isLoading && typeof onReady === 'function') {
            onReady();
        }
    }, [chartData, isLoading, onReady]);

    if (!chartData) {
        if (isLoading) {
            return (
                <div style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: window.innerWidth < 768 ? '300px' : '600px',
                }}>
                    <div style={{
                        textAlign: 'center',
                        padding: '2rem'
                    }}>
                        <div style={{
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #3498db',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 16px'
                        }}></div>
                        <p style={{
                            fontSize: '14px',
                            color: '#7f8c8d',
                            margin: 0
                        }}>
                            Loading...
                        </p>
                    </div>
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            );
        }

        // Show empty state only when not loading
        return (
            <div style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: window.innerWidth < 768 ? '300px' : '600px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px dashed #e0e0e0',
                padding: '1rem'
            }}>
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <h2 style={{
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        margin: '0 0 8px 0'
                    }}>
                        {isFeedback ? 'No Feedback Data Available' : 'No Categories Found'}
                    </h2>
                    <p style={{
                        fontSize: '16px',
                        color: '#7f8c8d',
                        margin: '8px 0 0 0',
                        maxWidth: '300px'
                    }}>
                        {isFeedback
                            ? 'No feedback distribution data found for the selected filters. Try adjusting your search criteria.'
                            : 'Great news! There are no categories matching your criteria. Everything looks good here! 😊'
                        }
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
                padding: '0.2rem',
                flexShrink: 0
            }}>
                <span style={{
                    fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem',
                    color: '#222',
                }}>
                    Click on any bar to filter by that category.
                </span>
                <select
                    value={topN}
                    onChange={(e) => setTopN(Number(e.target.value))}
                    style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '12px',
                        cursor: 'pointer',
                        backgroundColor: '#fff'
                    }}
                >
                    <option value={5}>Top 5</option>
                    <option value={10}>Top 10</option>
                    <option value={20}>Top 20</option>
                    <option value={50}>All</option>
                </select>
            </div>
            <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 10
                    }}>
                        <div style={{
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #3498db',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                    </div>
                )}

                <div style={{
                    width: '100%',
                    height: '100%'
                }}>
                    <Chart
                        options={chartOptions.options}
                        series={chartOptions.series}
                        type="bar"
                        height={chartHeight}
                        width="100%"
                    />
                </div>

                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }   
                `}</style>
            </div>
        </div>
    );
};

export default CategoryBarChart;